<template>
  <div class="space-y-4">
    <section v-if="!isSingleMode && !showEditor && !showCreate" class="app-section space-y-4">
      <div class="app-toolbar items-start justify-between gap-3">
        <div>
          <h2 class="m-0 text-xl font-semibold">Коммерческие предложения</h2>
          <p class="mt-1 text-sm text-muted-foreground">Управление основным и пользовательскими КП</p>
        </div>
        <button class="button" @click="startCreate">Новое</button>
      </div>

      <div v-if="isLoading" class="state-box">Загрузка...</div>
      <div v-else-if="proposals.length === 0" class="state-box">Коммерческие предложения пока не созданы</div>

      <div v-else class="commercial-grid">
        <article v-for="proposal in proposals" :key="proposal.id" class="proposal-card">
          <div class="proposal-card-head">
            <div class="min-w-0">
              <p class="proposal-id">ID {{ proposal.id }}</p>
              <p class="proposal-meta">
                {{ proposal.user_id ? `user_id: ${proposal.user_id}` : 'main (без user_id)' }}
              </p>
            </div>
            <span
              class="proposal-badge"
              :class="proposal.is_main ? 'proposal-badge-main' : 'proposal-badge-user'"
            >
              {{ proposal.is_main ? 'Main' : 'User' }}
            </span>
          </div>

          <div class="proposal-actions">
            <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="openEditor(proposal)">
              Редактировать
            </button>
            <button class="button button-danger !h-8 !px-3 !py-0 text-xs" @click="removeProposal(proposal)">
              Удалить
            </button>
          </div>
        </article>
      </div>
    </section>

    <section v-if="!isSingleMode && showCreate" class="app-section space-y-4">
      <div class="app-toolbar items-start justify-between gap-3">
        <div>
          <h2 class="m-0 text-xl font-semibold">Новое коммерческое предложение</h2>
          <p class="mt-1 text-sm text-muted-foreground">Заполните основные поля и прикрепите файлы</p>
        </div>
        <button class="button button-outline" @click="cancelCreate">← Назад</button>
      </div>

      <div class="form-grid">
        <div class="field-group md:col-span-2">
          <label class="field-label">user_id (опционально)</label>
          <input
            v-model="createForm.user_id"
            class="input-field"
            placeholder="UUID пользователя или пусто для main"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Frame contract file_id</label>
          <input v-model="createForm.frame_contract_file_id" class="input-field" placeholder="UUID файла" />
          <FileUploadField label="Загрузить файл" @file-uploaded="onCreateFrameUploaded" />
        </div>

        <div class="field-group">
          <label class="field-label">Commercial offer file_id</label>
          <input v-model="createForm.commercial_offer_file_id" class="input-field" placeholder="UUID файла" />
          <FileUploadField label="Загрузить файл" @file-uploaded="onCreateOfferUploaded" />
        </div>
      </div>

      <div class="form-actions">
        <button class="button button-outline" @click="cancelCreate">Отмена</button>
        <button class="button" @click="createProposal">Создать</button>
      </div>
    </section>

    <section v-if="isSingleMode && !showEditor" class="app-section space-y-4">
      <div class="app-toolbar items-start justify-between gap-3">
        <div>
          <h2 class="m-0 text-xl font-semibold">{{ editorTitle }}</h2>
          <p class="mt-1 text-sm text-muted-foreground">Загрузка и проверка коммерческого предложения</p>
        </div>
        <button class="button button-outline" @click="loadSingleModeProposal">Обновить</button>
      </div>

      <div v-if="isLoading" class="state-box">Загрузка...</div>
      <div v-else class="state-box state-box-left">
        <p class="m-0 text-foreground">{{ singleModeEmptyText }}</p>
        <button class="button mt-3" @click="createSingleModeProposal">Создать КП</button>
      </div>
    </section>

    <section v-if="showEditor && currentId" class="app-section space-y-4">
      <div class="editor-topbar">
        <div>
          <h2 class="m-0 text-xl font-semibold">{{ editorTitle }}</h2>
          <p class="mt-1 text-sm text-muted-foreground">Редактирование файлов и параметров коммерческого предложения</p>
        </div>

        <div class="editor-actions">
          <button
            v-if="showVisibilityToggle"
            class="button disabled:opacity-60"
            :class="isCommercialVisible ? 'button-success' : 'button-outline'"
            :disabled="isVisibilityUpdating"
            @click="toggleCommercialVisibility"
          >
            {{ visibilityButtonLabel }}
          </button>
          <button v-if="!isSingleMode" class="button button-outline" @click="closeEditor">← Назад</button>
        </div>
      </div>

      <div class="form-grid">
        <div v-if="!isSingleMode" class="field-group md:col-span-2">
          <label class="field-label">user_id</label>
          <input
            v-model="editForm.user_id"
            class="input-field"
            placeholder="UUID пользователя или пусто для main"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Frame contract file_id</label>
          <input v-model="editForm.frame_contract_file_id" class="input-field" placeholder="UUID файла" />
          <FileUploadField label="Загрузить файл" @file-uploaded="onEditFrameUploaded" />
        </div>

        <div class="field-group">
          <label class="field-label">Commercial offer file_id</label>
          <input v-model="editForm.commercial_offer_file_id" class="input-field" placeholder="UUID файла" />
          <FileUploadField label="Загрузить файл" @file-uploaded="onEditOfferUploaded" />
        </div>
      </div>

      <div class="form-actions">
        <button v-if="!isSingleMode" class="button button-outline" @click="closeEditor">Отмена</button>
        <button class="button" @click="saveProposal">Сохранить</button>
      </div>
    </section>

    <section v-if="showEditor && currentId" class="app-section space-y-4">
      <div class="app-toolbar items-center justify-between">
        <h3 class="m-0 text-lg font-semibold">Блоки</h3>
        <span class="text-sm text-muted-foreground">Всего блоков: {{ editForm.blocks.length }}</span>
      </div>

      <div v-if="editForm.blocks.length === 0" class="state-box">Нет блоков</div>

      <article v-for="block in editForm.blocks" :key="block.id" class="block-card space-y-3">
        <div class="form-grid">
          <div class="field-group">
            <label class="field-label">Текст</label>
            <textarea v-model="block.text" class="input-field textarea-field" />
          </div>

          <div class="field-group">
            <label class="field-label">Описание</label>
            <textarea v-model="block.description" class="input-field textarea-field" />
          </div>
        </div>

        <div class="project-grid">
          <div v-for="idx in 3" :key="idx" class="field-group">
            <label class="field-label">Проект {{ idx }}</label>
            <select v-model.number="block.project_ids[idx - 1]" class="input-field">
              <option value="" disabled>Выберите проект</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.id }} — {{ project.title }}
              </option>
            </select>
          </div>
        </div>

        <div class="proposal-actions">
          <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="saveBlock(block)">Сохранить</button>
          <button class="button button-danger !h-8 !px-3 !py-0 text-xs" @click="removeBlock(block)">Удалить</button>
        </div>
      </article>

      <div class="new-block-box space-y-3">
        <h4 class="m-0 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Новый блок</h4>

        <div class="form-grid">
          <div class="field-group">
            <label class="field-label">Текст</label>
            <textarea v-model="newBlock.text" class="input-field textarea-field" />
          </div>

          <div class="field-group">
            <label class="field-label">Описание</label>
            <textarea v-model="newBlock.description" class="input-field textarea-field" />
          </div>
        </div>

        <div class="project-grid">
          <div v-for="idx in 3" :key="idx" class="field-group">
            <label class="field-label">Проект {{ idx }}</label>
            <select v-model.number="newBlock.project_ids[idx - 1]" class="input-field">
              <option value="" disabled>Выберите проект</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.id }} — {{ project.title }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end">
          <button class="button" @click="addBlock">Добавить блок</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { fetchCommercials, createCommercial, fetchCommercial, updateCommercial, deleteCommercial, addCommercialBlock, updateCommercialBlock, deleteCommercialBlock, fetchProjects, updateUserApi } from '../services/api';
import FileUploadField from './FileUploadField.vue';

export default {
  name: 'CommercialView',
  props: {
    mode: {
      type: String,
      default: 'all',
      validator: (value) => ['all', 'main', 'user'].includes(value),
    },
    currentUserId: {
      type: String,
      default: '',
    },
    currentUsername: {
      type: String,
      default: '',
    },
    currentCommercialFilled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['commercial-filled-updated'],
  components: { FileUploadField },
  data() {
    return {
      proposals: [],
      isLoading: false,
      showCreate: false,
      showEditor: false,
      currentId: null,
      projects: [],
      createForm: {
        user_id: '',
        frame_contract_file_id: '',
        commercial_offer_file_id: '',
      },
      editForm: {
        user_id: '',
        frame_contract_file_id: '',
        commercial_offer_file_id: '',
        blocks: [],
      },
      newBlock: {
        text: '',
        description: '',
        project_ids: ['', '', ''],
      },
      isCommercialVisible: false,
      isVisibilityUpdating: false,
    };
  },
  computed: {
    isSingleMode() {
      return this.mode === 'main' || this.mode === 'user';
    },
    editorTitle() {
      if (this.mode === 'main') {
        return 'Редактор КП (Сайт)';
      }
      if (this.mode === 'user') {
        const username = this.currentUsername || 'username';
        return `Редактор КП (${username})`;
      }
      return 'Редактирование коммерческого';
    },
    singleModeEmptyText() {
      if (this.mode === 'main') {
        return 'Main КП не найдено.';
      }
      if (!this.currentUserId) {
        return 'Текущий пользователь не определен.';
      }
      const username = this.currentUsername || 'пользователь';
      return `КП пользователя ${username} не найдено.`;
    },
    showVisibilityToggle() {
      return this.mode === 'user' && Boolean(this.currentUserId);
    },
    visibilityButtonLabel() {
      return this.isCommercialVisible ? 'Скрыть КП' : 'Показать КП';
    },
  },
  watch: {
    mode() {
      this.load();
    },
    currentUserId() {
      if (this.mode === 'user') {
        this.loadSingleModeProposal();
      }
    },
    currentCommercialFilled(value) {
      this.isCommercialVisible = Boolean(value);
    },
  },
  created() {
    this.isCommercialVisible = Boolean(this.currentCommercialFilled);
    this.load();
  },
  methods: {
    async toggleCommercialVisibility() {
      const toast = useToast();
      if (!this.currentUserId || this.isVisibilityUpdating) {
        return;
      }
      const nextValue = !this.isCommercialVisible;
      try {
        this.isVisibilityUpdating = true;
        await updateUserApi(this.currentUserId, { commercial_filled: nextValue });
        this.isCommercialVisible = nextValue;
        this.$emit('commercial-filled-updated', nextValue);
        toast.success(nextValue ? 'КП показано' : 'КП скрыто');
      } catch (error) {
        console.error('Ошибка при обновлении видимости КП:', error);
        toast.error('Ошибка при обновлении видимости КП');
      } finally {
        this.isVisibilityUpdating = false;
      }
    },
    async load() {
      if (this.isSingleMode) {
        await this.loadSingleModeProposal();
        return;
      }
      try {
        this.isLoading = true;
        this.proposals = await fetchCommercials();
      } finally {
        this.isLoading = false;
      }
    },
    findSingleModeProposal(proposals) {
      if (this.mode === 'main') {
        return proposals.find((proposal) => proposal.is_main || !proposal.user_id) || null;
      }
      if (this.mode === 'user' && this.currentUserId) {
        return proposals.find((proposal) => String(proposal.user_id) === String(this.currentUserId)) || null;
      }
      return null;
    },
    async loadSingleModeProposal() {
      const toast = useToast();
      this.showEditor = false;
      this.currentId = null;
      try {
        this.isLoading = true;
        let proposals = await fetchCommercials();
        let targetProposal = this.findSingleModeProposal(proposals);

        // В пользовательском режиме создаем КП автоматически, если его нет.
        if (!targetProposal && this.mode === 'user' && this.currentUserId) {
          await createCommercial({
            user_id: this.currentUserId,
            frame_contract_file_id: null,
            commercial_offer_file_id: null,
          });
          proposals = await fetchCommercials();
          targetProposal = this.findSingleModeProposal(proposals);
        }

        if (!targetProposal) {
          return;
        }
        await this.openEditor(targetProposal);
      } catch (error) {
        this.showEditor = false;
        this.currentId = null;
        console.error('Ошибка при загрузке коммерческого:', error);
        toast.error('Ошибка при загрузке коммерческого');
      } finally {
        this.isLoading = false;
      }
    },
    async createSingleModeProposal() {
      const toast = useToast();
      if (this.mode === 'user' && !this.currentUserId) {
        toast.error('Текущий пользователь не определен');
        return;
      }
      try {
        const payload = {
          user_id: this.mode === 'main' ? null : this.currentUserId,
          frame_contract_file_id: null,
          commercial_offer_file_id: null,
        };
        await createCommercial(payload);
        toast.success('Коммерческое создано');
        await this.loadSingleModeProposal();
      } catch (error) {
        console.error('Ошибка при создании коммерческого:', error);
        toast.error('Ошибка при создании коммерческого');
      }
    },
    startCreate() {
      this.showCreate = true;
      this.createForm = {
        user_id: '',
        frame_contract_file_id: '',
        commercial_offer_file_id: '',
      };
    },
    cancelCreate() {
      this.showCreate = false;
    },
    async createProposal() {
      const toast = useToast();
      const payload = {
        user_id: this.createForm.user_id || null,
        frame_contract_file_id: this.createForm.frame_contract_file_id || null,
        commercial_offer_file_id: this.createForm.commercial_offer_file_id || null,
      };
      await createCommercial(payload);
      toast.success('Коммерческое создано');
      this.showCreate = false;
      await this.load();
    },
    async openEditor(proposal) {
      this.showEditor = true;
      this.currentId = proposal.id;
      await this.loadEditor(proposal.id);
    },
    async loadEditor(id) {
      const data = await fetchCommercial(id);
      this.editForm = {
        user_id: data.user_id || '',
        frame_contract_file_id: data.frame_contract_file_id || '',
        commercial_offer_file_id: data.commercial_offer_file_id || '',
        blocks: (data.blocks || []).map(block => ({
          id: block.id,
          text: block.text || '',
          description: block.description || '',
          project_ids: block.project_ids || ['', '', ''],
        })),
      };
      this.projects = await fetchProjects();
      this.newBlock = { text: '', description: '', project_ids: ['', '', ''] };
    },
    closeEditor() {
      this.showEditor = false;
      this.currentId = null;
    },
    async saveProposal() {
      const toast = useToast();
      const payload = {
        user_id: this.editForm.user_id || null,
        frame_contract_file_id: this.editForm.frame_contract_file_id || null,
        commercial_offer_file_id: this.editForm.commercial_offer_file_id || null,
      };
      if (this.mode === 'main') {
        payload.user_id = null;
      } else if (this.mode === 'user') {
        payload.user_id = this.currentUserId || null;
      }
      await updateCommercial(this.currentId, payload);
      toast.success('Коммерческое обновлено');
      if (this.isSingleMode) {
        await this.loadEditor(this.currentId);
      } else {
        await this.load();
      }
    },
    async removeProposal(proposal) {
      const toast = useToast();
      await deleteCommercial(proposal.id);
      toast.success('Коммерческое удалено');
      await this.load();
    },
    async addBlock() {
      const toast = useToast();
      const projectIds = this.newBlock.project_ids.map(id => Number(id));
      await addCommercialBlock(this.currentId, {
        text: this.newBlock.text || null,
        description: this.newBlock.description || null,
        project_ids: projectIds,
      });
      toast.success('Блок добавлен');
      await this.loadEditor(this.currentId);
    },
    async saveBlock(block) {
      const toast = useToast();
      const projectIds = block.project_ids.map(id => Number(id));
      await updateCommercialBlock(this.currentId, block.id, {
        text: block.text || null,
        description: block.description || null,
        project_ids: projectIds,
      });
      toast.success('Блок обновлен');
      await this.loadEditor(this.currentId);
    },
    async removeBlock(block) {
      const toast = useToast();
      await deleteCommercialBlock(this.currentId, block.id);
      toast.success('Блок удален');
      await this.loadEditor(this.currentId);
    },
    onCreateFrameUploaded(file) {
      if (file && file.id) {
        this.createForm.frame_contract_file_id = file.id;
      }
    },
    onCreateOfferUploaded(file) {
      if (file && file.id) {
        this.createForm.commercial_offer_file_id = file.id;
      }
    },
    onEditFrameUploaded(file) {
      if (file && file.id) {
        this.editForm.frame_contract_file_id = file.id;
      }
    },
    onEditOfferUploaded(file) {
      if (file && file.id) {
        this.editForm.commercial_offer_file_id = file.id;
      }
    },
  }
};
</script>

<style scoped>
.state-box {
  @apply rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground;
}

.state-box-left {
  @apply text-left;
}

.commercial-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.proposal-card {
  @apply rounded-lg border bg-background p-4 shadow-sm;
}

.proposal-card-head {
  @apply flex items-start justify-between gap-3;
}

.proposal-id {
  @apply m-0 text-sm font-semibold text-foreground;
  word-break: break-all;
}

.proposal-meta {
  @apply mt-1 text-xs text-muted-foreground;
  word-break: break-all;
}

.proposal-badge {
  @apply inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold;
}

.proposal-badge-main {
  @apply bg-success/15 text-success;
}

.proposal-badge-user {
  @apply bg-muted text-muted-foreground;
}

.proposal-actions {
  @apply mt-3 flex flex-wrap items-center justify-end gap-2;
}

.editor-topbar {
  @apply flex flex-wrap items-start justify-between gap-3;
}

.editor-actions {
  @apply flex flex-wrap items-center gap-2;
}

.form-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
}

.project-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.field-group {
  @apply space-y-2;
}

.field-label {
  @apply block text-sm font-medium text-foreground;
}

.form-actions {
  @apply flex flex-wrap items-center justify-end gap-2 border-t pt-4;
}

.block-card {
  @apply rounded-lg border bg-background p-4 shadow-sm;
}

.new-block-box {
  @apply rounded-lg border border-dashed bg-muted/30 p-4;
}

.textarea-field {
  @apply min-h-[96px] resize-y;
}

@media (min-width: 960px) {
  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .project-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
